import {
    alpha,
    Box,
    Container,
    Divider,
    Stack,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { motion, Variants } from 'framer-motion';
import React, { useEffect, useRef, useState } from 'react';
import CountUp from 'react-countup';
import ApplicationButton from '../../../../Components/ApplicationButton';
import { getLocalizationFromDescriptor } from '../../../../Helpers/TextDescriptorUtils';
import { getOrDefault } from '../../../../Helpers/Utils';
import { useMicrospecThemeLanguageContext } from '../../../../Themes';
import { HeroWithStatsBarProps, StatItem } from './types';

// ─── Stat value parser ─────────────────────────────────────────────────────────
// Splits strings like "25+", "10,000+", "100%" into CountUp-compatible parts.

interface ParsedStat {
    end: number;
    prefix: string;
    suffix: string;
    separator: string;
    decimals: number;
}

const parseStatValue = (raw: string): ParsedStat => {
    // Groups: (optional prefix)(numeric with optional commas/dot)(optional suffix)
    const match = raw.match(/^([^0-9]*)([0-9][0-9,.]*)([^0-9]*)$/);
    if (!match) return { end: 0, prefix: '', suffix: raw, separator: '', decimals: 0 };
    const [, prefix, numStr, suffix] = match;
    const hasSeparator = numStr.includes(',');
    const cleanNum = numStr.replace(/,/g, '');
    const end = parseFloat(cleanNum);
    const decimals = cleanNum.includes('.')
        ? cleanNum.split('.')[1].length
        : 0;
    return {
        end: isNaN(end) ? 0 : end,
        prefix,
        suffix,
        separator: hasSeparator ? ',' : '',
        decimals,
    };
};

// ─── Animation variants ────────────────────────────────────────────────────────

const contentVariants: Variants = {
    hidden: { opacity: 0 },
    show: {
        opacity: 1,
        transition: { staggerChildren: 0.18, delayChildren: 0.25 },
    },
};

const itemVariants: Variants = {
    hidden: { opacity: 0, y: 40 },
    show: {
        opacity: 1,
        y: 0,
        transition: { type: 'spring', stiffness: 90, damping: 18 },
    },
};

const statsBarVariants: Variants = {
    hidden: { opacity: 0, y: 30 },
    show: {
        opacity: 1,
        y: 0,
        transition: { delay: 0.8, duration: 0.6, ease: 'easeOut' },
    },
};

// ─── Component ────────────────────────────────────────────────────────────────

const HeroWithStatsBar: React.FC<HeroWithStatsBarProps> = ({ spec, type, index }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const { localizationProvider } = useMicrospecThemeLanguageContext();

    // Scroll-blur state (mirrors HeroSectionWithScrollTrigger)
    const sectionId = useRef(`hero-stats-bar-${Math.random().toString(36).slice(2)}`);
    const [scrollOffset, setScrollOffset] = useState(0);

    useEffect(() => {
        const id = sectionId.current;
        const handleScroll = () => {
            const el = document.getElementById(id);
            if (!el) return;
            const top = window.scrollY + el.getBoundingClientRect().top;
            setScrollOffset(Math.max(0, window.scrollY - top));
        };
        handleScroll();
        window.addEventListener('scroll', handleScroll, { passive: true });
        window.addEventListener('resize', handleScroll, { passive: true });
        return () => {
            window.removeEventListener('scroll', handleScroll);
            window.removeEventListener('resize', handleScroll);
        };
    }, []);

    // ── Spec extraction ─────────────────────────────────────────────────────────

    const backgroundImageSource = getOrDefault(spec.backgroundImageSource, '');
    const backgroundVideoSource = getOrDefault(spec.backgroundVideoSource, '');
    const backgroundColor = getOrDefault(spec.backgroundColor, theme.palette.background.default);
    const backgroundFilter = getOrDefault(spec.backgroundFilter, null);
    const accentColor = getOrDefault(spec.accentColor, theme.palette.primary.main);

    const hasBackgroundMedia = !!(backgroundImageSource || backgroundVideoSource);
    const defaultTextColor = hasBackgroundMedia ? theme.palette.common.white : theme.palette.text.primary;

    const titleText = localizationProvider.translateByType(
        getLocalizationFromDescriptor(spec.title, { type: 'Literal', text: '' })
    );
    const subtitleText = localizationProvider.translateByType(
        getLocalizationFromDescriptor(spec.subtitle, { type: 'Literal', text: '' })
    );
    const titleColor = getOrDefault(spec.titleColor, defaultTextColor);
    const subtitleColor = getOrDefault(spec.subtitleColor, alpha(defaultTextColor, 0.85));

    const actionButtonText = localizationProvider.translateByType(
        getLocalizationFromDescriptor(spec.actionButtonText, { type: 'Literal', text: '' })
    );
    const actionButtonTextColor = getOrDefault(spec.actionButtonTextColor, theme.palette.primary.contrastText);
    const actionButtonBackground = getOrDefault(spec.actionButtonBackground, theme.palette.primary.main);
    const actionButtonHref = getOrDefault(spec.actionButtonHref, '#');

    const secondaryActionButtonText = localizationProvider.translateByType(
        getLocalizationFromDescriptor(spec.secondaryActionButtonText, { type: 'Literal', text: '' })
    );
    const secondaryActionButtonTextColor = getOrDefault(spec.secondaryActionButtonTextColor, defaultTextColor);
    const secondaryActionButtonBackground = getOrDefault(spec.secondaryActionButtonBackground, 'transparent');
    const secondaryActionButtonHref = getOrDefault(spec.secondaryActionButtonHref, '#');

    const stats: StatItem[] = getOrDefault(spec.stats, []);
    const statsBarBackgroundColor = getOrDefault(
        spec.statsBarBackgroundColor,
        theme.palette.mode === 'dark' ? 'rgba(18,18,18,0.92)' : 'rgba(0,0,0,0.82)'
    );
    const statsBarTextColor = getOrDefault(spec.statsBarTextColor, alpha(theme.palette.common.white, 0.75));
    const statsBarValueColor = getOrDefault(spec.statsBarValueColor, theme.palette.common.white);

    const showScrollIndicator = getOrDefault(spec.showScrollIndicator, true);

    // ── Scroll-blur helpers ─────────────────────────────────────────────────────

    const blurAmount = scrollOffset > 0 ? Math.min(scrollOffset * 0.1, 30) : 0;
    const scaleAmount = scrollOffset > 0 ? Math.min(1 + scrollOffset * 0.001, 1.3) : 1;

    const mediaStyle: React.CSSProperties = {
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        display: 'block',
        filter: blurAmount > 0 ? `blur(${blurAmount}px)` : undefined,
        transform: scaleAmount !== 1 ? `scale(${scaleAmount})` : undefined,
        transition: 'filter 0.3s ease-out, transform 0.3s ease-out',
    };

    // ── Render ──────────────────────────────────────────────────────────────────

    return (
        <Box
            id={spec.id ?? sectionId.current}
            sx={{
                position: 'relative',
                height: '100vh',
                overflow: 'hidden',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: !hasBackgroundMedia ? backgroundColor : undefined,
            }}
        >
            {/* ── Animated orbs (no-media fallback) ── */}
            {!hasBackgroundMedia && (
                <>
                    {[
                        { top: '10%', left: '-5%', size: 600, duration: '25s', keyframe: 'hws-float-a', move: 'translate(100px,100px) scale(1.1)' },
                        { bottom: '10%', right: '-5%', size: 500, duration: '20s', keyframe: 'hws-float-b', move: 'translate(-80px,-80px) scale(1.15)' },
                        { top: '50%', right: '20%', size: 400, duration: '22s', keyframe: 'hws-float-c', move: 'translate(-60px,60px) scale(1.08)' },
                    ].map(({ size, duration, keyframe, move, ...pos }, i) => (
                        <Box
                            key={i}
                            sx={{
                                position: 'absolute',
                                ...pos,
                                width: size,
                                height: size,
                                borderRadius: '50%',
                                background: `radial-gradient(circle, ${alpha(accentColor, 0.15 - i * 0.02)}, transparent)`,
                                filter: 'blur(70px)',
                                animation: `${keyframe} ${duration} ease-in-out infinite`,
                                [`@keyframes ${keyframe}`]: {
                                    '0%, 100%': { transform: 'translate(0,0) scale(1)' },
                                    '50%': { transform: move },
                                },
                            }}
                        />
                    ))}
                </>
            )}

            {/* ── Background video ── */}
            {backgroundVideoSource && (
                <Box
                    component="video"
                    autoPlay
                    loop
                    muted
                    playsInline
                    // poster={backgroundImageSource || undefined}
                    sx={{ position: 'absolute', inset: 0, zIndex: 0 }}
                    style={mediaStyle}
                    preload="metadata"
                >
                    <source src={backgroundVideoSource} type="video/mp4" />
                </Box>
            )}

            {/* ── Background image ── */}
            {backgroundImageSource && !backgroundVideoSource && (
                <Box
                    sx={{ position: 'absolute', inset: 0, zIndex: 0, overflow: 'hidden' }}
                >
                    <Box
                        sx={{
                            position: 'absolute',
                            inset: 0,
                            backgroundImage: `url(${backgroundImageSource})`,
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                            backgroundRepeat: 'no-repeat',
                        }}
                        style={{
                            filter: blurAmount > 0 ? `blur(${blurAmount}px)` : undefined,
                            transform: scaleAmount !== 1 ? `scale(${scaleAmount})` : undefined,
                            transition: 'filter 0.3s ease-out, transform 0.3s ease-out',
                        }}
                    />
                </Box>
            )}

            {/* ── Background filter overlay ── */}
            {backgroundFilter && hasBackgroundMedia && (
                <Box
                    sx={{
                        position: 'absolute',
                        inset: 0,
                        zIndex: 1,
                        pointerEvents: 'none',
                        backgroundColor:
                            backgroundFilter.type === 'darken'
                                ? `rgba(0,0,0,${backgroundFilter.intensity})`
                                : `rgba(255,255,255,${backgroundFilter.intensity})`,
                    }}
                />
            )}

            {/* ── Default dark overlay when media present and no explicit filter ── */}
            {hasBackgroundMedia && !backgroundFilter && (
                <Box
                    sx={{
                        position: 'absolute',
                        inset: 0,
                        zIndex: 1,
                        pointerEvents: 'none',
                        backgroundColor:
                            theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.5)' : 'rgba(0,0,0,0.42)',
                    }}
                />
            )}

            {/* ── Main centered content ── */}
            <Container
                maxWidth="lg"
                sx={{
                    position: 'relative',
                    zIndex: 2,
                    // Leave room for stats bar (~120 px) so content stays visually centred
                    pb: '120px',
                }}
            >
                <motion.div variants={contentVariants} initial="hidden" animate="show">
                    <Stack alignItems="center" spacing={3} sx={{ textAlign: 'center' }}>
                        {/* Title */}
                        {titleText && (
                            <motion.div variants={itemVariants}>
                                <Typography
                                    variant="h1"
                                    sx={{
                                        color: titleColor,
                                        fontWeight: 900,
                                        fontSize: { xs: '2.4rem', sm: '3.4rem', md: '4.5rem', lg: '5.5rem' },
                                        lineHeight: 1.1,
                                        letterSpacing: '-0.02em',
                                        textShadow: hasBackgroundMedia
                                            ? '0 4px 20px rgba(0,0,0,0.7), 0 2px 8px rgba(0,0,0,0.5)'
                                            : 'none',
                                    }}
                                >
                                    {titleText}
                                </Typography>
                            </motion.div>
                        )}

                        {/* Subtitle */}
                        {subtitleText && (
                            <motion.div variants={itemVariants}>
                                <Typography
                                    variant="h5"
                                    sx={{
                                        color: subtitleColor,
                                        fontWeight: 500,
                                        fontSize: { xs: '1.15rem', sm: '1.45rem', md: '1.75rem' },
                                        lineHeight: 1.5,
                                        maxWidth: 720,
                                        mx: 'auto',
                                        textShadow: hasBackgroundMedia
                                            ? '0 2px 10px rgba(0,0,0,0.5)'
                                            : 'none',
                                    }}
                                >
                                    {subtitleText}
                                </Typography>
                            </motion.div>
                        )}

                        {/* CTA buttons */}
                        {(actionButtonText || secondaryActionButtonText) && (
                            <motion.div variants={itemVariants}>
                                <Stack
                                    direction={{ xs: 'column', sm: 'row' }}
                                    gap={2}
                                    justifyContent="center"
                                    alignItems="center"
                                    sx={{ mt: 1 }}
                                >
                                    {actionButtonText && (
                                        <ApplicationButton
                                            href={actionButtonHref}
                                            variant="contained"
                                            size="large"
                                            sx={{
                                                background: `linear-gradient(45deg, ${actionButtonBackground}, ${alpha(actionButtonBackground, 0.8)})`,
                                                color: actionButtonTextColor,
                                                px: { xs: 3.5, sm: 5 },
                                                py: { xs: 1.5, sm: 1.8 },
                                                fontSize: { xs: '1rem', sm: '1.1rem' },
                                                fontWeight: 700,
                                                borderRadius: 3,
                                                textTransform: 'none',
                                                minWidth: { xs: 200, sm: 220 },
                                                boxShadow: `0 8px 28px ${alpha(actionButtonBackground, 0.45)}`,
                                                position: 'relative',
                                                overflow: 'hidden',
                                                '&::before': {
                                                    content: '""',
                                                    position: 'absolute',
                                                    top: 0,
                                                    left: '-100%',
                                                    width: '100%',
                                                    height: '100%',
                                                    background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.25), transparent)',
                                                    transition: 'left 0.5s ease',
                                                },
                                                '&:hover': {
                                                    transform: 'translateY(-2px)',
                                                    boxShadow: `0 14px 38px ${alpha(actionButtonBackground, 0.6)}`,
                                                    '&::before': { left: '100%' },
                                                },
                                                '&:active': { transform: 'translateY(0)' },
                                            }}
                                        >
                                            {actionButtonText}
                                        </ApplicationButton>
                                    )}
                                    {secondaryActionButtonText && (
                                        <ApplicationButton
                                            href={secondaryActionButtonHref}
                                            variant="outlined"
                                            size="large"
                                            sx={{
                                                color: secondaryActionButtonTextColor,
                                                borderColor: secondaryActionButtonTextColor,
                                                backgroundColor: secondaryActionButtonBackground,
                                                px: { xs: 3.5, sm: 5 },
                                                py: { xs: 1.5, sm: 1.8 },
                                                fontSize: { xs: '1rem', sm: '1.1rem' },
                                                fontWeight: 600,
                                                borderRadius: 3,
                                                textTransform: 'none',
                                                minWidth: { xs: 200, sm: 220 },
                                                borderWidth: 2,
                                                backdropFilter: 'blur(8px)',
                                                '&:hover': {
                                                    borderWidth: 2,
                                                    transform: 'translateY(-2px)',
                                                    backgroundColor: alpha(secondaryActionButtonTextColor, 0.1),
                                                    boxShadow: `0 8px 24px ${alpha(secondaryActionButtonTextColor, 0.3)}`,
                                                },
                                                '&:active': { transform: 'translateY(0)' },
                                            }}
                                        >
                                            {secondaryActionButtonText}
                                        </ApplicationButton>
                                    )}
                                </Stack>
                            </motion.div>
                        )}
                    </Stack>
                </motion.div>
            </Container>

            {/* ── Stats bar ── */}
            {stats.length > 0 && (
                <Box
                    component={motion.div}
                    variants={statsBarVariants}
                    initial="hidden"
                    animate="show"
                    sx={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        zIndex: 3,
                        backgroundColor: statsBarBackgroundColor,
                        backdropFilter: 'blur(16px)',
                        borderTop: `1px solid ${alpha(theme.palette.common.white, 0.12)}`,
                        borderRadius: '16px 16px 0 0',
                        boxShadow: '0 -8px 32px rgba(0,0,0,0.4)',
                    }}
                >
                    <Container maxWidth="lg">
                        <Stack
                            direction={{ xs: 'column', sm: 'row' }}
                            divider={
                                <Divider
                                    orientation={isMobile ? 'horizontal' : 'vertical'}
                                    flexItem
                                    sx={{
                                        borderColor: alpha(theme.palette.common.white, 0.15),
                                        my: { xs: 0, sm: 1.5 },
                                        mx: { xs: 'auto', sm: 0 },
                                        width: { xs: '40%', sm: 'auto' },
                                    }}
                                />
                            }
                            justifyContent="center"
                            alignItems="center"
                            sx={{ py: { xs: 2, sm: 2.5 } }}
                        >
                            {stats.map((stat, i) => {
                                const value = localizationProvider.translateByType(
                                    getLocalizationFromDescriptor(stat.value, { type: 'Literal', text: '' })
                                );
                                const label = localizationProvider.translateByType(
                                    getLocalizationFromDescriptor(stat.label, { type: 'Literal', text: '' })
                                );
                                return (
                                    <Box
                                        key={i}
                                        component={motion.div}
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ delay: 0.9 + i * 0.12, duration: 0.5, ease: 'easeOut' }}
                                        sx={{
                                            flex: 1,
                                            textAlign: 'center',
                                            px: { xs: 2, sm: 3, md: 4 },
                                            py: { xs: 1, sm: 0 },
                                        }}
                                    >
                                    <Typography
                                            variant="h4"
                                            component="p"
                                            sx={{
                                                color: statsBarValueColor,
                                                fontWeight: 800,
                                                fontSize: { xs: '1.6rem', sm: '1.9rem', md: '2.2rem' },
                                                lineHeight: 1.15,
                                                letterSpacing: '-0.01em',
                                                mb: 0.5,
                                            }}
                                        >
                                            {(() => {
                                                const parsed = parseStatValue(value);
                                                return (
                                                    <CountUp
                                                        end={parsed.end}
                                                        prefix={parsed.prefix}
                                                        suffix={parsed.suffix}
                                                        separator={parsed.separator}
                                                        decimals={parsed.decimals}
                                                        duration={2.5}
                                                        enableScrollSpy
                                                        scrollSpyOnce
                                                        scrollSpyDelay={i * 120}
                                                    />
                                                );
                                            })()}
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            component="p"
                                            sx={{
                                                color: statsBarTextColor,
                                                fontSize: { xs: '0.78rem', sm: '0.85rem', md: '0.92rem' },
                                                fontWeight: 500,
                                                letterSpacing: '0.04em',
                                                textTransform: 'uppercase',
                                            }}
                                        >
                                            {label}
                                        </Typography>
                                    </Box>
                                );
                            })}
                        </Stack>
                    </Container>
                </Box>
            )}

            {/* ── Scroll indicator ── */}
            {showScrollIndicator && (
                <Box
                    component={motion.div}
                    animate={{ y: [0, 10, 0] }}
                    transition={{ duration: 1.6, repeat: Infinity, ease: 'easeInOut' }}
                    sx={{
                        position: 'absolute',
                        bottom: stats.length > 0 ? 'calc(120px + 24px)' : 24,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        zIndex: 4,
                        display: { xs: 'none', sm: 'flex' },
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 0.5,
                        opacity: 0.7,
                    }}
                >
                    {/* Mouse icon */}
                    <Box
                        sx={{
                            width: 24,
                            height: 38,
                            borderRadius: 12,
                            border: `2px solid ${alpha(defaultTextColor, 0.6)}`,
                            display: 'flex',
                            justifyContent: 'center',
                            pt: 1,
                        }}
                    >
                        <Box
                            component={motion.div}
                            animate={{ y: [0, 8, 0], opacity: [1, 0, 1] }}
                            transition={{ duration: 1.6, repeat: Infinity, ease: 'easeInOut' }}
                            sx={{
                                width: 4,
                                height: 8,
                                borderRadius: 2,
                                backgroundColor: alpha(defaultTextColor, 0.8),
                            }}
                        />
                    </Box>
                </Box>
            )}
        </Box>
    );
};

export default HeroWithStatsBar;
